import { useFormBehaviors } from 'hooks';
import { UseFormBehaviors } from 'hooks/useFormBehaviors/useFormBehaviors.types';
import { useTranslation } from 'hooks/useTranslation';
import { useFetchAssignedGroupedPositionsContext } from 'pages/LocationSettingsPage/PositionsAllocation/FetchAssignedGroupedPositions.data.context';
import { usePositionsAllocationSettingsPageDataContext } from 'pages/LocationSettingsPage/PositionsAllocation/PositionsAllocationSettingsPage.data.context';
import { PositionsAllocationSettingsPageFormValues as ALSPFFields } from 'pages/LocationSettingsPage/PositionsAllocation/PositionsAllocationSettingsPageForm.types';
import { createContext, FC, useContext, useMemo } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { API_POST_updateCustomerPositionsAllocation } from 'requests/POST_updatePositionsAllocation';
import { toastService } from 'services';
import { useAPIActions } from 'store/reducers/api/apiSlice';
import { handleGenericErrorMessage } from 'utils/handleGenericErrorMessage';

const PositionsAllocationSettingsPageFormContext = createContext<UseFormBehaviors<ALSPFFields, {}>>(
  {} as UseFormBehaviors<ALSPFFields, {}>,
);

export const PositionsAllocationSettingsPageFormProvider: FC = ({ children }) => {
  const { data, fetch } = usePositionsAllocationSettingsPageDataContext();
  const { data: groupedPositionsData } = useFetchAssignedGroupedPositionsContext();
  const { locationId } = useParams<{ locationId: string }>();
  const { apiRequest } = useAPIActions();
  const history = useHistory();
  const { t } = useTranslation();
  const existingPositionIds =
    data?.fetchCommunity?.locationPositions.map((position) => position.id) || [];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const initialState = groupedPositionsData?.groupPositions?.edges?.reduce((acc: any, group) => {
    group.node?.items &&
      group.node.items.forEach((position) => {
        acc[`positionLocation_${position.id}`] = data?.fetchCommunity?.locationPositions?.some(
          (locPosition) => locPosition.id === position.id,
        );
      });
    return acc;
  }, {});

  const form = useFormBehaviors<ALSPFFields, {}>({
    initialState: { ...initialState },
    onSubmit: async (formValues: ALSPFFields) => {
      const selectedPositionIds = Object.entries(formValues)
        .filter(([key, value]) => key.startsWith('positionLocation_') && value === true)
        .map(([key]) => parseInt(key.replace('positionLocation_', ''), 10));
      const updatePositionIds = [...existingPositionIds, ...selectedPositionIds];
      const result = await apiRequest(
        API_POST_updateCustomerPositionsAllocation(locationId, updatePositionIds),
        {
          onSuccess: () => {
            fetch();
            history.push(`/settings/locations/${locationId}/positions-allocation`);
            toastService.success(`${t('success:POSITIONS_SUCCESSFULLY_ALLOCATED')}`);
          },
          onError: (e) =>
            handleGenericErrorMessage(e, t('errors:FAILED_TO_UPDATE_POSITIONS_ALLOCATION')),
        },
      );
      return result;
    },
    validations: {},
  });

  const isDisabled = useMemo(() => {
    return Object.entries(form?.values ?? {}).every(
      ([key, value]) => key?.startsWith?.('positionLocation_') && value === initialState?.[key],
    );
  }, [form?.values, initialState]);

  return (
    <PositionsAllocationSettingsPageFormContext.Provider value={{ ...form, isDisabled }}>
      {children}
    </PositionsAllocationSettingsPageFormContext.Provider>
  );
};

export const usePositionsAllocationSettingsPageFormContext = () =>
  useContext(PositionsAllocationSettingsPageFormContext);
